import { getAppEnvironment } from "../config/app-environment";
import { getFeatureFlags } from "../config/features";
import { chapterRangeFor } from "../data/chapter-range";
import { UNIVERSE_CHAPTER_ID } from "../data/compile-book-universe";
import {
  joinCompiledChapters,
  WHOLE_BOOK_SPAN_ID,
  type TimelineSpan,
} from "../data/join-chapters";
import {
  loadBookBundles,
  loadChapterBundle,
} from "../data/load-compiled-bundle";
import type { CompiledChapter, JourneyMode } from "../data/schema/compiled";
import { buildChapterBoard, type ChapterBoard } from "../lib/timeline/board";
import { formatChapterHeading, parseChapterRoute } from "../lib/timeline/book/ChapterId";
import type { BookSummary } from "../data/book-summary";
import { readTimelineUiPreferences } from "../lib/timeline/view/TimelineUiPreferences/prefs";

export interface BookTimelineModel {
  /** Every compiled chapter. The view joins whichever are selected. */
  chapters: CompiledChapter[];
  spans: TimelineSpan[];
  initialSpanId: string;
  title: string;
  loadUniverse: () => Promise<BookJourneyModel>;
  /** Loaded up front when the viewer left on Universe, so a refresh stays there. */
  initialUniverse?: BookJourneyModel;
}

export interface BookJourneyModel {
  chapter: CompiledChapter;
  spans: TimelineSpan[];
  board?: ChapterBoard;
}

/** A book's address is every compiled chapter on one rail. A chapter's selects that chapter. */
export async function loadBookTimeline(
  bookId: string,
  chapterParam: string | undefined,
): Promise<BookTimelineModel> {
  const prefs = readTimelineUiPreferences();
  const initialUniverse = prefs.journey === "universe"
    && getFeatureFlags(getAppEnvironment()).universeJourneyView
    ? await loadUniverse(bookId).catch(() => undefined)
    : undefined;
  const chapters = await loadBookBundles(bookId);
  const initialSpanId = spanIdForRoute(bookId, chapterParam, chapters);
  const { spans } = joinCompiledChapters(chapters);
  return {
    title: await bookTitle(bookId, chapterParam),
    chapters,
    spans,
    initialSpanId: chapterParam === undefined
      && prefs.spanId !== null && chapterRangeFor(spans, prefs.spanId) !== null
      ? prefs.spanId
      : initialSpanId,
    loadUniverse: () => loadUniverse(bookId),
    ...(initialUniverse === undefined ? {} : { initialUniverse }),
  };
}

async function bookTitle(bookId: string, chapterParam: string | undefined): Promise<string> {
  const response = await fetch(`/book-data/${bookId}/book.json`);
  const summary = await response.json() as BookSummary;
  const chapter = summary.chapters.find((item) => item.id === chapterParam);
  return chapter === undefined
    ? summary.title
    : `${formatChapterHeading(chapter.id, chapter.title, ": ")} | ${summary.title}`;
}

/** Building a board is the slowest step, so it waits until a view actually needs it. */
export function journeyModel(
  chapter: CompiledChapter,
  spans: TimelineSpan[],
  journey: JourneyMode,
): BookJourneyModel {
  let built: ChapterBoard | undefined;
  return {
    chapter,
    spans,
    get board(): ChapterBoard {
      built ??= buildChapterBoard(chapter, journey);
      return built;
    },
  };
}

export async function loadUniverse(bookId: string): Promise<BookJourneyModel> {
  const chapter = await loadChapterBundle(bookId, UNIVERSE_CHAPTER_ID);
  const last = chapter.eventsByJourney.universe.at(-1);
  const endMs = last === undefined ? 0 : last.playbackMs + last.durationMs;
  const spans = [{ id: UNIVERSE_CHAPTER_ID, label: "Universe", startMs: 0, endMs }];
  return journeyModel(chapter, spans, "universe");
}

export function spanIdForRoute(
  bookId: string,
  chapterParam: string | undefined,
  chapters: readonly { chapterId: string }[],
): string {
  if (chapterParam === undefined) {
    return WHOLE_BOOK_SPAN_ID;
  }
  const chapterId = parseChapterRoute(chapterParam);
  if (!chapters.some((chapter) => chapter.chapterId === chapterId)) {
    throw new Error(
      `No bundle for ${bookId} ${chapterId}. ` +
        "Run npm run data:compile, then npm run data:stage-public before deploy.",
    );
  }
  return chapterId;
}
